/**
 * L_INCORPORATIONS.INCORPORATION_DATA → Incorporation DOC (M32014) live values.
 */
import {
  resolveShareClass,
  resolvePurpose,
  resolveCompanyType,
  resolveCompanySubType,
  resolveRace as resolveRaceOpt,
  resolveIdType,
} from '../components/cdd/cddOptions';

function trim(v: unknown): string {
  return String(v ?? '').trim();
}

/** 성별 정규화 — M/1/male → Male, F/2/female → Female, 그 외 원값 유지. */
function normalizeGender(v: unknown): string {
  const s = String(v ?? '').trim();
  if (!s) return '';
  const u = s.toUpperCase();
  if (u === 'M' || u === '1' || u === 'MALE' || u === 'MAN') return 'Male';
  if (u === 'F' || u === '2' || u === 'FEMALE' || u === 'WOMAN') return 'Female';
  return s;
}

function isCodeValue(val: string): boolean {
  if (!val) return false;
  if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(val)) return true;
  if (/^[A-Z]\d{2,5}$/i.test(val)) return true;
  return false;
}

function safePart(raw: unknown, name?: unknown): string {
  if (name != null && trim(name) && !isCodeValue(trim(name))) return trim(name);
  const s = trim(raw);
  return s && !isCodeValue(s) ? s : '';
}

function formatAddress(addr: Record<string, any> | null | undefined): string {
  if (!addr || typeof addr !== 'object') return '';
  const lines = [
    trim(addr.street01 || addr.addressLine1 || addr.blockHouseNo),
    trim(addr.street02 || addr.addressLine2 || addr.streetName),
    trim(addr.street03 || addr.addressLine3 || addr.buildingEstateName),
  ].filter(Boolean);
  const loc = [
    safePart(addr.postcode ?? addr.postalCode, addr.postcodeName),
    safePart(addr.city ?? addr.town, addr.cityName),
    safePart(addr.state, addr.stateName),
    safePart(addr.country, addr.countryName),
  ].filter(Boolean);
  return [...lines, loc.join(' ')].filter(Boolean).join(', ');
}

function personName(p: any): string {
  // 법인은 개인 이름 칸이 비어 있다. 법인명은 최상위에도, myDetails 안쪽에도 올 수 있어
  // 둘 다 본다 - 예전에는 최상위만 봐서 법인 주주의 문서명이 "CDD - .docx" 로 나왔다.
  const corp = trim(
    p?.corporateName
    || p?.myShareholderDetails?.corporateName
    || p?.myDetails?.corporateName
    // SSM 미등록 법인은 corporateName 이 비고 entityName 에만 이름이 있다.
    || p?.entityName
    || p?.myShareholderDetails?.entityName
    || p?.myDetails?.entityName,
  );
  if (corp) return corp;
  // 개인 이름은 T-Link 가 편집하는 firstName/lastName 을 **그대로(이름→성)** 쓴다.
  // 말레이 단일 Full Name 전환 후 firstName 에 전체이름, lastName 은 빈값이라
  // firstName+lastName = 화면 표기 = 문서 표기 가 되어 일치한다.
  // 저장본 p.name 은 과거 2칸 입력 때 [lastName, firstName] 로 뒤집혀 캐시된 값이 섞여 있어
  // (예: firstName='YEO'/lastName='STEVE' 인데 p.name='STEVE YEO'),
  // 그걸 먼저 쓰면 문서가 T-Link 와 어긋난다 → firstName+lastName 를 우선, name 은 폴백.
  const joined = [p?.firstName, p?.lastName].filter(Boolean).map(trim).join(' ');
  return joined || trim(p?.name);
}

function resolveId(p: any, md: Record<string, any>): { idType: string; idNo: string } {
  const idType = trim(
    md.idType ||
      md.id_type ||
      p.idType ||
      p.idTypeCd ||
      (p.passport || p.passportNo ? 'Passport' : p.nric || p.nricNo ? 'NRIC' : ''),
  );
  const idNo = trim(
    md.idNumber ||
      md.id_number ||
      md.idNo ||
      md.nricNo ||
      md.passportNo ||
      md.contactRegNo ||
      p.identificationNo ||
      p.idNo ||
      p.director_id_no ||
      p.nricNo ||
      p.passportNo ||
      p.contactRegNo ||
      p.CONTACT_REG_NO ||
      p.passport ||
      p.nric ||
      '',
  );
  return { idType, idNo };
}

function resolveNationality(p: any, md: Record<string, any>): string {
  return trim(
    md.nationalityName ||
      md.nationality ||
      p.nationalityName ||
      p.nationality ||
      p.nationalityCd ||
      '',
  );
}

function resolveDob(p: any, md: Record<string, any>): string {
  return trim(md.dateOfBirth || md.dob || p.dateOfBirth || p.dob || '');
}

function resolveRace(p: any, md: Record<string, any>): string {
  return resolveRaceOpt(md.raceName || md.race || p.race || '');
}

function resolvePersonAddress(p: any, md: Record<string, any>): string {
  const fromMd = formatAddress(md.residentialAddress || md.address || md._address);
  if (fromMd) return fromMd;
  return formatAddress(p._address || p.address || p.residentialAddress);
}

export interface IncDocAutofillResult {
  values: Record<string, string>;
  directorRows: number[];
  memberRows: number[];
}

export interface MapIncorporationToIncDocOptions {
  /** MSIC/SSIC code → display name (Business Nature) */
  activityNameByCode?: Map<string, string>;
}

/**
 * Map incorporation detail JSON into live DOC field keys (company_name, dir1_*, mem1_*, …).
 */
export function mapIncorporationToIncDocValues(
  data: Record<string, any> | null | undefined,
  options?: MapIncorporationToIncDocOptions,
): IncDocAutofillResult {
  const values: Record<string, string> = {};
  if (!data || typeof data !== 'object') {
    return { values, directorRows: [1], memberRows: [1] };
  }

  // Proposed company name = 회사명만 (suffix 제외)
  //   companyFullName / name+suffix 조합은 Company Type·Sub Type 과 중복되므로 쓰지 않음.
  const suffixRaw = trim(
    data.companySuffixName ||
      data.companySuffix ||
      data.sub_type ||
      data.subType ||
      '',
  );
  const suffixLabel = resolveCompanySubType(suffixRaw);
  let companyName = trim(data.companyName || data.company_name);
  if (!companyName && trim(data.companyFullName)) {
    let full = trim(data.companyFullName);
    if (suffixLabel) {
      const re = new RegExp(`\\s*${suffixLabel.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\s*$`, 'i');
      full = full.replace(re, '').trim();
    }
    companyName = full;
  }
  if (companyName) values.company_name = companyName;

  // Purpose — NEW_INCORPORATION → NEW INCORPORATION
  const purpose = resolvePurpose(data.purpose || data.applicationPurpose || '');
  if (purpose) values.purpose = purpose;

  // Company Type — LIMITED_BY_SHARES (suffix 절대 fallback 금지)
  const companyType = resolveCompanyType(
    data.company_type || data.companyType || data.companyTypeName || '',
  );
  if (companyType) values.company_type = companyType;

  // Sub Type — SDN. BHD. 등 (suffix / sub_type)
  const subType = resolveCompanySubType(
    data.sub_type ||
      data.subType ||
      data.companySubTypeName ||
      data.companySubType ||
      data.companySuffixName ||
      data.companySuffix ||
      '',
  );
  if (subType) values.company_sub_type = subType;

  const bizDesc = trim(
    data.businessDescription ||
      data.business_description ||
      data.directIncorporationFormData?.applicationDetails?.businessDescription ||
      data.applicationDetails?.businessDescription,
  );
  if (bizDesc) values.business_description = bizDesc;

  // MSIC / activities — Business Nature = 코드명, MSIC Code = 숫자 코드
  const activityMap =
    options?.activityNameByCode instanceof Map ? options.activityNameByCode : new Map<string, string>();

  const pickMsicCode = (...cands: unknown[]): string => {
    for (const c of cands) {
      const s = trim(c);
      if (/^\d{4,5}$/.test(s)) return s;
    }
    return '';
  };
  const pickNatureName = (code: string, ...cands: unknown[]): string => {
    for (const c of cands) {
      const s = trim(c);
      if (s && !/^\d{4,5}$/.test(s) && !isCodeValue(s)) return s;
    }
    if (code && activityMap.has(code)) return activityMap.get(code) || '';
    return '';
  };

  const code1 = pickMsicCode(
    data.msic_1,
    data.msic_code_1,
    data.primaryActivityCode,
    data.primaryActivity,
  );
  const name1 = pickNatureName(
    code1,
    data.business_1,
    data.business_nature_1,
    data.primaryActivityName,
    data.primaryActivityDesc,
    data.primaryActivityDescription,
    // primaryActivity 가 이미 설명 문자열인 경우만
    /^\d{4,5}$/.test(trim(data.primaryActivity)) ? '' : data.primaryActivity,
  );
  if (name1) values.business_nature_1 = name1;
  if (code1) values.msic_code_1 = code1;

  const code2 = pickMsicCode(
    data.msic_2,
    data.msic_code_2,
    data.secondaryActivityCode,
    data.secondaryActivity,
  );
  const name2 = pickNatureName(
    code2,
    data.business_2,
    data.business_nature_2,
    data.secondaryActivityName,
    data.secondaryActivityDesc,
    data.secondaryActivityDescription,
    /^\d{4,5}$/.test(trim(data.secondaryActivity)) ? '' : data.secondaryActivity,
  );
  if (name2) values.business_nature_2 = name2;
  if (code2) values.msic_code_2 = code2;

  const code3 = pickMsicCode(data.msic_3, data.msic_code_3, data.tertiaryActivityCode, data.tertiaryActivity);
  const name3 = pickNatureName(
    code3,
    data.business_3,
    data.business_nature_3,
    data.tertiaryActivityName,
  );
  if (name3) values.business_nature_3 = name3;
  if (code3) values.msic_code_3 = code3;

  const addr = data.address && typeof data.address === 'object' ? data.address : null;
  const regAddr = formatAddress(addr);
  if (regAddr) values.registered_address = regAddr;
  const regEmail = trim(addr?.email || addr?.registeredAddressEmail || data.registeredEmail || data.email || data.companyEmail || data.contactEmail);
  if (regEmail) values.registered_email = regEmail;
  const regOffice = trim(addr?.officeNumber || addr?.registeredOfficeNumber || data.registeredOfficeNo);
  if (regOffice) values.registered_office_no = regOffice;
  const regFax = trim(addr?.faxNumber || addr?.registeredFaxNumber || data.registeredFaxNo);
  if (regFax) values.registered_fax_no = regFax;

  const hasBiz =
    addr &&
    (trim(addr.businessAddressLine1) ||
      trim(addr.businessAddressLine2) ||
      trim(addr.businessCity) ||
      trim(addr.businessPostcode));
  if (hasBiz) {
    const biz = formatAddress({
      addressLine1: addr.businessAddressLine1,
      addressLine2: addr.businessAddressLine2,
      addressLine3: addr.businessAddressLine3,
      postcode: addr.businessPostcode,
      city: addr.businessCity,
      state: addr.businessState,
      country: addr.businessCountry,
      postcodeName: addr.businessPostcodeName,
      cityName: addr.businessCityName,
      stateName: addr.businessStateName,
      countryName: addr.businessCountryName,
    });
    if (biz) values.business_address = biz;
    const bizEmail = trim(addr.businessEmail || data.businessEmail || data.email || regEmail);
    if (bizEmail) values.business_email = bizEmail;
    const bizOffice = trim(addr.businessOfficeNumber);
    if (bizOffice) values.business_office_no = bizOffice;
    const bizFax = trim(addr.businessFaxNumber);
    if (bizFax) values.business_fax_no = bizFax;
  } else if (regAddr) {
    values.business_address = regAddr;
    if (regEmail) values.business_email = regEmail;
  }

  const capital = trim(data.minimumCapital || data.shareCapital || data.totalNoOfShares);
  if (capital) values.total_no_of_shares = capital;

  const directors = Array.isArray(data.directors) ? data.directors : [];
  const directorRows: number[] = directors.length ? directors.map((_, i) => i + 1) : [1];
  directors.forEach((d: any, i: number) => {
    const id = i + 1;
    const md =
      (d?.myDirectorDetails && typeof d.myDirectorDetails === 'object' ? d.myDirectorDetails : null) ||
      (d?.myDetails && typeof d.myDetails === 'object' ? d.myDetails : {}) ||
      {};
    const name = personName(d);
    if (name) values[`dir${id}_name`] = name;
    const { idType, idNo } = resolveId(d, md);
    const idTypeResolved = resolveIdType(idType);
    if (idTypeResolved) values[`dir${id}_id_type`] = idTypeResolved;
    if (idNo) values[`dir${id}_id_no`] = idNo;
    const nat = resolveNationality(d, md);
    if (nat) values[`dir${id}_nationality`] = nat;
    const dob = resolveDob(d, md);
    if (dob) values[`dir${id}_dob`] = dob;
    const race = resolveRace(d, md);
    if (race) values[`dir${id}_race`] = race;
    const email = trim(d.email || md.email);
    if (email) values[`dir${id}_email`] = email;
    const gender = normalizeGender(d.gender ?? md.gender ?? d.details?.gender ?? d.myDetails?.gender ?? d.sex ?? md.sex ?? d.genderName ?? md.genderName ?? d.genderCode ?? md.genderCode);
    if (gender) values[`dir${id}_gender`] = gender;
    const paddr = resolvePersonAddress(d, md);
    if (paddr) values[`dir${id}_address`] = paddr;
  });

  const shareholders = Array.isArray(data.shareholders) ? data.shareholders : [];
  const memberRows: number[] = shareholders.length ? shareholders.map((_, i) => i + 1) : [1];
  shareholders.forEach((s: any, i: number) => {
    const id = i + 1;
    const md =
      (s?.myShareholderDetails && typeof s.myShareholderDetails === 'object'
        ? s.myShareholderDetails
        : null) ||
      (s?.myDetails && typeof s.myDetails === 'object' ? s.myDetails : {}) ||
      {};
    const name = personName(s);
    if (name) values[`mem${id}_name`] = name;
    const { idType, idNo } = resolveId(s, md);
    const idTypeResolved = resolveIdType(idType);
    if (idTypeResolved) values[`mem${id}_id_type`] = idTypeResolved;
    if (idNo) values[`mem${id}_id_no`] = idNo;
    const nat = resolveNationality(s, md);
    if (nat) values[`mem${id}_nationality`] = nat;
    const race = resolveRace(s, md);
    if (race) values[`mem${id}_race`] = race;
    const email = trim(s.email || md.email);
    if (email) values[`mem${id}_email`] = email;
    const gender = normalizeGender(s.gender ?? md.gender ?? s.details?.gender ?? s.myDetails?.gender ?? s.sex ?? md.sex ?? s.genderName ?? md.genderName ?? s.genderCode ?? md.genderCode);
    if (gender) values[`mem${id}_gender`] = gender;
    const paddr = resolvePersonAddress(s, md);
    if (paddr) values[`mem${id}_address`] = paddr;
    const cls = trim(md.classOfShare || s.classOfShare || s.shareType || s.shareholderType);
    if (cls) values[`mem${id}_class_of_share`] = resolveShareClass(cls);
    const nos = trim(s.numberOfShares || s.shares || md.numberOfShares);
    if (nos) values[`mem${id}_no_of_shares`] = nos;
    const pps = trim(md.pricePerShare || s.pricePerShare);
    if (pps) values[`mem${id}_price_per_share`] = pps;
  });

  // Signer default: first director
  if (values.dir1_name && !values.signer_name) {
    values.signer_name = values.dir1_name;
    if (values.dir1_id_no) values.signer_nric_passport_no = values.dir1_id_no;
  }

  return { values, directorRows, memberRows };
}
